import {
  MissingRecordError,
  type AnnotationRecord,
  type DocumentRecord,
  type PageTextCacheRecord,
  type PdfBlobRecord,
  type ReadingStateRecord,
  type Result,
  failure,
  success,
} from "@/domain";

import type { DatabaseInvalidationPublisher } from "./broadcast-channel";
import type { MarginaliaDatabase } from "./database";
import { toStorageWriteError } from "./storage-errors";

export type DocumentDataSnapshot = {
  document: DocumentRecord;
  pdfBlob?: PdfBlobRecord | null;
  annotations?: AnnotationRecord[];
  readingState?: ReadingStateRecord | null;
  pageTextCache?: PageTextCacheRecord[];
};

export type BestEffortDocumentCreation = {
  document: DocumentRecord;
  pdfStored: boolean;
  retentionError: Error | null;
};

function assertSnapshotOwnership(snapshot: DocumentDataSnapshot): void {
  const documentId = snapshot.document.id;
  const hasPdfBlob =
    snapshot.pdfBlob !== undefined && snapshot.pdfBlob !== null;

  if ((snapshot.document.pdfAvailability === "stored") !== hasPdfBlob) {
    throw new TypeError(
      "Snapshot PDF availability must agree with its stored PDF record.",
    );
  }

  if (
    hasPdfBlob &&
    snapshot.pdfBlob !== undefined &&
    snapshot.pdfBlob !== null &&
    snapshot.pdfBlob.documentId !== documentId
  ) {
    throw new TypeError("The stored PDF belongs to another document.");
  }

  for (const annotation of snapshot.annotations ?? []) {
    if (annotation.documentId !== documentId) {
      throw new TypeError("An imported annotation belongs to another document.");
    }
  }

  if (
    snapshot.readingState !== undefined &&
    snapshot.readingState !== null &&
    snapshot.readingState.documentId !== documentId
  ) {
    throw new TypeError("The reading state belongs to another document.");
  }

  for (const cache of snapshot.pageTextCache ?? []) {
    if (cache.documentId !== documentId) {
      throw new TypeError("A page-text cache belongs to another document.");
    }
  }
}

export class LocalDataService {
  constructor(
    private readonly db: MarginaliaDatabase,
    private readonly invalidation?: DatabaseInvalidationPublisher,
  ) {}

  async createDocument(
    record: DocumentRecord,
    pdfBlob?: PdfBlobRecord | null,
  ): Promise<void> {
    if (pdfBlob && pdfBlob.documentId !== record.id) {
      throw new TypeError("The stored PDF belongs to another document.");
    }

    if ((record.pdfAvailability === "stored") !== Boolean(pdfBlob)) {
      throw new TypeError(
        "Document PDF availability must agree with the stored PDF record.",
      );
    }

    try {
      await this.db.transaction(
        "rw",
        this.db.documents,
        this.db.pdfBlobs,
        async () => {
          await this.db.documents.add(record);
          if (pdfBlob) {
            if (pdfBlob.blob.size !== pdfBlob.byteLength) {
              throw new TypeError(
                "Stored PDF byteLength must match the Blob size.",
              );
            }
            await this.db.pdfBlobs.add(pdfBlob);
          }
        },
      );
    } catch (error) {
      if (error instanceof TypeError) {
        throw error;
      }
      throw toStorageWriteError(error, "create a document");
    }

    this.publishDocumentChanged(record.id);
  }

  async createDocumentWithBestEffortPdf(
    record: DocumentRecord,
    pdfBlob?: PdfBlobRecord | null,
  ): Promise<Result<BestEffortDocumentCreation, Error>> {
    const externalRecord: DocumentRecord = {
      ...record,
      pdfAvailability: "external",
    };

    try {
      await this.createDocument(externalRecord);
    } catch (error) {
      return failure(error instanceof Error ? error : new Error(String(error)));
    }

    if (!pdfBlob) {
      return success({
        document: externalRecord,
        pdfStored: false,
        retentionError: null,
      });
    }

    try {
      const stored = await this.retainPdf(pdfBlob, record.updatedAt);
      return success({
        document: stored,
        pdfStored: true,
        retentionError: null,
      });
    } catch (error) {
      return success({
        document: externalRecord,
        pdfStored: false,
        retentionError:
          error instanceof Error ? error : new Error(String(error)),
      });
    }
  }

  async retainPdf(
    pdfBlob: PdfBlobRecord,
    updatedAt: string,
  ): Promise<DocumentRecord> {
    let updated: DocumentRecord;

    try {
      updated = await this.db.transaction(
        "rw",
        this.db.documents,
        this.db.pdfBlobs,
        async () => {
          const document = await this.db.documents.get(pdfBlob.documentId);
          if (!document) {
            throw new MissingRecordError("Document", pdfBlob.documentId);
          }

          if (pdfBlob.blob.size !== pdfBlob.byteLength) {
            throw new TypeError(
              "Stored PDF byteLength must match the Blob size.",
            );
          }

          const next: DocumentRecord = {
            ...document,
            pdfAvailability: "stored",
            updatedAt,
          };
          await this.db.pdfBlobs.put(pdfBlob);
          await this.db.documents.put(next);
          return next;
        },
      );
    } catch (error) {
      if (error instanceof MissingRecordError || error instanceof TypeError) {
        throw error;
      }
      throw toStorageWriteError(error, "retain a local PDF");
    }

    this.publishDocumentChanged(pdfBlob.documentId);
    return updated;
  }

  async removeRetainedPdf(
    documentId: string,
    updatedAt: string,
  ): Promise<DocumentRecord> {
    let updated: DocumentRecord;

    try {
      updated = await this.db.transaction(
        "rw",
        this.db.documents,
        this.db.pdfBlobs,
        async () => {
          const document = await this.db.documents.get(documentId);
          if (!document) {
            throw new MissingRecordError("Document", documentId);
          }

          const next: DocumentRecord = {
            ...document,
            pdfAvailability: "external",
            updatedAt,
          };
          await this.db.pdfBlobs.delete(documentId);
          await this.db.documents.put(next);
          return next;
        },
      );
    } catch (error) {
      if (error instanceof MissingRecordError) {
        throw error;
      }
      throw toStorageWriteError(error, "remove a retained PDF");
    }

    this.publishDocumentChanged(documentId);
    return updated;
  }

  async deleteDocument(documentId: string): Promise<void> {
    try {
      await this.db.transaction(
        "rw",
        this.db.documents,
        this.db.pdfBlobs,
        this.db.annotations,
        this.db.readingStates,
        this.db.pageTextCache,
        async () => {
          await Promise.all([
            this.db.pdfBlobs.delete(documentId),
            this.db.annotations.where("documentId").equals(documentId).delete(),
            this.db.readingStates.delete(documentId),
            this.db.pageTextCache
              .where("documentId")
              .equals(documentId)
              .delete(),
          ]);
          await this.db.documents.delete(documentId);
        },
      );
    } catch (error) {
      throw toStorageWriteError(error, "delete a document and its local data");
    }

    this.invalidation?.publish({
      type: "document-deleted",
      documentId,
    });
  }

  async replaceAnnotations(
    documentId: string,
    records: AnnotationRecord[],
  ): Promise<void> {
    for (const record of records) {
      if (record.documentId !== documentId) {
        throw new TypeError("An annotation belongs to another document.");
      }
    }

    try {
      await this.db.transaction("rw", this.db.annotations, async () => {
        await this.db.annotations
          .where("documentId")
          .equals(documentId)
          .delete();
        await this.db.annotations.bulkAdd(records);
      });
    } catch (error) {
      throw toStorageWriteError(error, "replace document annotations");
    }

    this.publishDocumentChanged(documentId);
  }

  async importDocumentSnapshot(snapshot: DocumentDataSnapshot): Promise<void> {
    assertSnapshotOwnership(snapshot);
    const tables = [
      this.db.documents,
      this.db.pdfBlobs,
      this.db.annotations,
      this.db.readingStates,
      this.db.pageTextCache,
    ] as const;

    try {
      await this.db.transaction("rw", tables, async () => {
        await this.db.documents.put(snapshot.document);

        if (snapshot.pdfBlob) {
          await this.db.pdfBlobs.put(snapshot.pdfBlob);
        }

        if (snapshot.annotations?.length) {
          await this.db.annotations.bulkPut(snapshot.annotations);
        }

        if (snapshot.readingState) {
          await this.db.readingStates.put(snapshot.readingState);
        }

        if (snapshot.pageTextCache?.length) {
          await this.db.pageTextCache.bulkPut(snapshot.pageTextCache);
        }
      });
    } catch (error) {
      throw toStorageWriteError(error, "import a document snapshot");
    }

    this.publishDocumentChanged(snapshot.document.id);
  }

  async restoreDocumentSnapshot(snapshot: DocumentDataSnapshot): Promise<void> {
    assertSnapshotOwnership(snapshot);
    const documentId = snapshot.document.id;
    const tables = [
      this.db.documents,
      this.db.pdfBlobs,
      this.db.annotations,
      this.db.readingStates,
      this.db.pageTextCache,
    ] as const;

    try {
      await this.db.transaction("rw", tables, async () => {
        await Promise.all([
          this.db.annotations.where("documentId").equals(documentId).delete(),
          this.db.pageTextCache
            .where("documentId")
            .equals(documentId)
            .delete(),
          this.db.readingStates.delete(documentId),
          this.db.pdfBlobs.delete(documentId),
        ]);

        await this.db.documents.put(snapshot.document);
        if (snapshot.pdfBlob) {
          await this.db.pdfBlobs.put(snapshot.pdfBlob);
        }
        if (snapshot.annotations?.length) {
          await this.db.annotations.bulkAdd(snapshot.annotations);
        }
        if (snapshot.readingState) {
          await this.db.readingStates.put(snapshot.readingState);
        }
        if (snapshot.pageTextCache?.length) {
          await this.db.pageTextCache.bulkAdd(snapshot.pageTextCache);
        }
      });
    } catch (error) {
      throw toStorageWriteError(error, "restore a document snapshot");
    }

    this.publishDocumentChanged(documentId);
  }

  private publishDocumentChanged(documentId: string): void {
    this.invalidation?.publish({
      type: "document-changed",
      documentId,
    });
  }
}
